import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon, ReviewButton } from '../common';
import ReviewListModal from './ReviewListModal';
import ServiceBanner from './ServiceBanner';
import { reviewService } from '../../api/services';

/**
 * Shared layout for all 6 transfer landing pages.
 * All measurements are exact Figma specs (960px content, 1280px hero).
 */
export default function TransferLanding({ heroImg, children, brandDesc }) {
  const { t } = useTranslation();
  const location = useLocation();

  // 첫 화면 서비스 설명(2줄, SEO용 가시 텍스트). 시트("첫 화면 문구와 방식") 확정 카피 — 언어별 직접 입력(DeepL 미사용).
  const SERVICE_KEYS = ['airport', 'golf', 'resort', 'hospital', 'pet', 'business'];
  // 홈(/)=공항 랜딩이므로 루트도 airport 로 인식.
  const introKey = location.pathname === '/'
    ? 'airport'
    : SERVICE_KEYS.find((k) => location.pathname.endsWith(`/transfer/${k}`));

  // #241: 메인(이동 랜딩) 차량정보 슬라이드 = '완전 고정값'(이미지 URL + 문구 하드코딩).
  //   → 어드민(차량관리 > 차량정보 이미지) 수정의 영향을 받지 않음. (부킹 페이지 등 공통 차량정보
  //     섹션은 기존대로 어드민 이미지 연동 유지 — 거긴 손대지 않음.)
  //   운영 시 이미지/문구 교체는 이 배열만 수정.
  const S3 = 'https://vipvantour-images.s3.ap-northeast-2.amazonaws.com/images/';
  const vehicleSlideMeta = [
    // #241: 클라 요청 — 차량 #1 히어로 이미지(0c67846d)를 첫 슬라이드 PREMIUM VAN 으로 (기존 EXTERIOR 와 동일 이미지였어 중복 제거)
    { tag: 'PREMIUM VAN', title: t('transfer_landing.vehicle_premium_van_title'), desc: t('transfer_landing.vehicle_premium_van_desc'), image_url: S3 + '0c67846d4428408aada2c5e7d70d5230.webp' },
    { tag: 'PREMIUM SEAT', title: t('transfer_landing.vehicle_premium_seat_title'), desc: t('transfer_landing.vehicle_premium_seat_desc'), image_url: S3 + '3e05b012308f41d79788aacac6b48888.webp' },
    { tag: 'SPACIOUS TRUNK', title: t('transfer_landing.vehicle_trunk_title'), desc: t('transfer_landing.vehicle_trunk_desc'), image_url: S3 + 'a110d522c4b14081abe270afd9b07b1d.webp' },
    { tag: 'LUXURY INTERIOR', title: t('transfer_landing.vehicle_interior_title'), desc: t('transfer_landing.vehicle_interior_desc'), image_url: S3 + '2cf8bd537c9f4184b5f0836c340693ad.webp' },
    { tag: 'COMFORTABLE RIDE', title: t('transfer_landing.vehicle_ride_title'), desc: t('transfer_landing.vehicle_ride_desc'), image_url: S3 + '32e86871589e4566bd26ab5a84a51c18.webp' },
    { tag: 'SMART SYSTEM', title: t('transfer_landing.vehicle_smart_title'), desc: t('transfer_landing.vehicle_smart_desc'), image_url: S3 + 'f4993fd4d9974ed6a494aa1d0a261872.webp' },
    { tag: 'SAFETY FIRST', title: t('transfer_landing.vehicle_safety_title'), desc: t('transfer_landing.vehicle_safety_desc'), image_url: S3 + 'a29d3d01918c48e09faee7ed996f8428.webp' },
  ];

  const serviceFeatures = [
    { id: 1, icon: 'garage-check', title: t('transfer_landing.feature_corp_title'), desc: t('transfer_landing.feature_corp_desc') },
    { id: 2, icon: 'license', title: t('transfer_landing.feature_license_title'), desc: t('transfer_landing.feature_license_desc') },
    { id: 3, icon: 'person-shield', title: t('transfer_landing.feature_safety_title'), desc: t('transfer_landing.feature_safety_desc') },
    { id: 4, icon: 'id-card', title: t('transfer_landing.feature_certified_title'), desc: t('transfer_landing.feature_certified_desc') },
  ];

  const serviceTabs = [
    { key: 'airport', label: t('service.airport'), icon: 'airport', to: '/' }, // 홈=공항 통합: 루트로 이동
    { key: 'golf', label: t('service.golf'), icon: 'golf', to: '/transfer/golf' },
    { key: 'ski', label: t('service.resort'), icon: 'ski', to: '/transfer/resort' },
    { key: 'hospital', label: t('service.hospital'), icon: 'hospital', to: '/transfer/hospital' },
    { key: 'pet', label: t('service.pet'), icon: 'pet', to: '/transfer/pet' },
    { key: 'business', label: t('service.business'), icon: 'business', to: '/transfer/business' },
  ];

  // #241: 고정값 그대로 사용(어드민 fetch 제거). image_url 은 vehicleSlideMeta 에 하드코딩됨.
  const [vehicleSlides] = useState(vehicleSlideMeta.map((m, i) => ({ id: i + 1, ...m })));
  const [rawVehicleIdx, setRawVehicleIdx] = useState(vehicleSlides.length > 1 ? 1 : 0);
  const [vehicleTransition, setVehicleTransition] = useState(true);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [avgRating, setAvgRating] = useState('');
  const vehicleTouchStart = useRef(0);
  const vehicleTouchEnd = useRef(0);

  useEffect(() => {
    reviewService.getStats().then((res) => {
      if (res.average_rating) setAvgRating(res.average_rating);
    }).catch(() => {});
    // 서비스별 고정 배너는 ServiceBanner 컴포넌트가 자체 조회(service+lang). 여기선 슬라이더 갤러리 fetch 제거.
    // #241: 차량 이미지 어드민 API 조회 제거 — 메인 슬라이드는 고정값으로만 운영(어드민 영향 차단).
  }, []);

  // Infinite-loop vehicle carousel
  const vLen = vehicleSlides.length;
  const extendedSlides = vLen > 1
    ? [vehicleSlides[vLen - 1], ...vehicleSlides, vehicleSlides[0]]
    : vehicleSlides;
  const slidePercent = extendedSlides.length > 0 ? 100 / extendedSlides.length : 100;
  const vehicleDisplayIdx = vLen > 1 ? ((rawVehicleIdx - 1) % vLen + vLen) % vLen : 0;

  const handleVehicleTransitionEnd = () => {
    if (rawVehicleIdx <= 0) {
      setVehicleTransition(false);
      setRawVehicleIdx(vLen);
    } else if (rawVehicleIdx >= vLen + 1) {
      setVehicleTransition(false);
      setRawVehicleIdx(1);
    }
  };

  useEffect(() => {
    if (!vehicleTransition) {
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setVehicleTransition(true));
      });
      return () => cancelAnimationFrame(id);
    }
  }, [vehicleTransition]);

  // 차량 소개 자동 슬라이드 (4초 간격)
  useEffect(() => {
    if (vLen <= 1) return;
    const timer = setInterval(() => {
      setRawVehicleIdx((i) => i + 1);
    }, 4000);
    return () => clearInterval(timer);
  }, [vLen]);


  return (
    <div className="min-h-screen bg-white">
      {/* ===== Hero Image (1280x296, r:16) - desktop only ===== */}
      <section className="hidden lg:block max-w-container mx-auto px-5">
        <div className="h-[296px] bg-mono-200 rounded-2xl overflow-hidden relative">
          {/* CWV: hero 를 webp(96% 경량) 우선 + png 폴백. 데스크탑 전용 흐림 배경(장식, alt=""). */}
          <picture>
            <source srcSet={`/images/icon/${heroImg}.webp`} type="image/webp" />
            <img
              src={`/images/icon/${heroImg}.png`}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
            />
          </picture>
          {/* Full blur overlay (Figma: 배경흐림 레이어) — 선명도 ↑ 위해 2px→1px */}
          <div className="absolute inset-0 backdrop-blur-[1px]" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-white/30" />
        </div>
      </section>

      {/* ===== Dark Card (960x390, r:12, bg:#2E3442, shadow) ===== */}
      <section className="max-w-content mx-auto lg:px-5 lg:-mt-[236px] relative z-10">
        <div className="bg-[#555B65]/95 lg:rounded-xl lg:shadow-dark-card shadow-[0_4px_20px_rgba(0,0,0,0.08)] backdrop-blur-sm">
          {/* Service Tabs: each 100x40, icon 20px, gap:0, r:8 8 0 0 */}
          <div className="flex overflow-hidden lg:rounded-t-xl">
            {serviceTabs.map((tab) => {
              // 공항 탭은 / 와 /transfer/airport(직접 방문) 양쪽에서 활성.
              const isActive = location.pathname === tab.to
                || (tab.key === 'airport' && location.pathname === '/transfer/airport');
              return (
                <Link
                  key={tab.key}
                  to={tab.to}
                  className={`relative flex-1 lg:flex-none lg:w-[100px] h-[58px] lg:h-10 flex flex-col lg:flex-row items-center justify-center gap-0.5 lg:gap-1 py-2 text-[13px] lg:text-[15px] font-medium lg:font-semibold transition lg:rounded-t-lg ${
                    isActive
                      ? 'bg-white text-orange-500 lg:bg-white'
                      : 'text-[#C8CCD2] hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Icon name={tab.icon} size={22} />
                  <span className="text-center leading-tight break-keep lg:whitespace-nowrap lg:tracking-wider">{tab.label}</span>
                </Link>
              );
            })}
          </div>

          {/* White Form Card (960x350, r:0 0 12 12, p:20) */}
          <div className="bg-white lg:rounded-b-xl p-4 sm:p-5">
            <div className="flex flex-col gap-4 sm:gap-5">
              {children}
            </div>
          </div>
        </div>
      </section>

      {/* ===== 서비스 설명 문구 (2줄, SEO 가시 텍스트 — 검색 아래) ===== */}
      {introKey && (
        <section className="max-w-content mx-auto px-4 sm:px-5 py-6 sm:py-8 text-center">
          <p className="text-[14px] sm:text-base font-semibold text-mono-800 break-keep leading-snug">
            {t(`transfer_intro.${introKey}.line1`)}
          </p>
          <p className="mt-1 text-[13px] sm:text-sm text-mono-600 break-keep leading-snug">
            {t(`transfer_intro.${introKey}.line2`)}
          </p>
        </section>
      )}

      {/* ===== 서비스별 고정 광고 배너 (2026-06 개편 — 슬라이딩 3열 갤러리 대체) =====
           ServiceBanner 가 service+언어로 배너 1장 조회, 데스크탑/모바일 이미지 자동 전환. 미등록 시 미노출. */}
      <ServiceBanner service={introKey} />

      {/* ===== Review Button (960x56) ===== */}
      <section className="max-w-content mx-auto px-4 sm:px-5 pb-8 sm:pb-10">
        <ReviewButton title={t('transfer_landing.review_title')} rating={avgRating} onClick={() => setShowReviewModal(true)} />
      </section>

      {/* ===== Brand Section — 카피라이팅 계층 구조 (흰 배경) ===== */}
      <section className="max-w-content mx-auto px-4 sm:px-5 py-10 sm:py-16">
        <div className="flex flex-col items-center text-center gap-5 sm:gap-7">
          {/* 상단 카피 — 점진적 크기 상승 */}
          <div className="flex flex-col items-center gap-1.5 sm:gap-2">
            <p className="text-base sm:text-lg font-medium text-mono-600 tracking-wide break-keep">
              {t('transfer_landing.copy_line1')}
            </p>
            <p className="text-base sm:text-lg font-semibold text-mono-700 tracking-wide break-keep">
              {t('transfer_landing.copy_line2')}
            </p>
            <p className="text-lg sm:text-2xl font-bold text-mono-900 leading-snug break-keep">
              {t('transfer_landing.copy_line3')}
            </p>
          </div>

          {/* 구분선 */}
          <div className="w-12 border-t-2 border-orange-400" />

          {/* Row 43: 브랜드 로고 이미지 (흰 배경 → 원본 색상 그대로) */}
          <h2 className="sr-only">VIP VAN TOUR</h2>
          <img src="/images/logo/logo2.png" alt="VIP VAN TOUR" className="h-10 sm:h-14 w-auto" />

          {/* 슬로건 */}
          <p className="font-lato text-[17px] sm:text-[22px] font-black text-[#203864] italic tracking-wide">
            {t('transfer_landing.tagline')}
          </p>
        </div>
      </section>

      {/* ===== Vehicle Showcase (960x264, r:12, bg:#F8F9FC) ===== */}
      {vLen > 0 && (
        <section className="max-w-content mx-auto px-4 sm:px-5 pb-10 sm:pb-16">
          <div
            className="relative bg-mono-50 rounded-xl overflow-hidden"
            onTouchStart={(e) => { vehicleTouchStart.current = e.touches[0].clientX; }}
            onTouchMove={(e) => { vehicleTouchEnd.current = e.touches[0].clientX; }}
            onTouchEnd={() => {
              const diff = vehicleTouchStart.current - vehicleTouchEnd.current;
              if (Math.abs(diff) > 50) {
                if (diff > 0) setRawVehicleIdx((i) => i + 1);
                else setRawVehicleIdx((i) => i - 1);
              }
              vehicleTouchStart.current = 0;
              vehicleTouchEnd.current = 0;
            }}
          >
            {/* Sliding track — infinite loop */}
            <div
              className={`flex ${vehicleTransition ? 'transition-transform duration-500 ease-in-out' : ''}`}
              style={{
                width: `${extendedSlides.length * 100}%`,
                transform: `translateX(-${rawVehicleIdx * slidePercent}%)`,
              }}
              onTransitionEnd={handleVehicleTransitionEnd}
            >
              {extendedSlides.map((s, i) => (
                <div key={i} style={{ width: `${slidePercent}%` }} className="shrink-0 flex flex-col lg:flex-row items-center">
                  <div className="w-full lg:w-[475px] h-[200px] lg:h-[264px] shrink-0 bg-mono-200 lg:rounded-xl flex items-center justify-center lg:m-4 overflow-hidden">
                    {s.image_url ? (
                      <img src={s.image_url} alt={s.title || ''} loading="lazy" className="w-full h-full object-cover" />
                    ) : (
                      <>
                        <span className="text-sm font-medium text-mono-400 lg:hidden">{t('transfer_landing.vehicle_image')}</span>
                        <Icon name="image" size={48} className="text-mono-400 hidden lg:block" />
                      </>
                    )}
                  </div>
                  <div className="flex-1 w-full flex flex-col items-start px-5 pt-4 pb-2 lg:py-5 gap-2.5">
                    {/* #277: 스페인어 등 긴 제목이 모바일(≥390px)에서 2줄로 넘치지 않게 3개 텍스트 비례 축소.
                        제목 21→18px 가 1줄 임계(실측). eyebrow/설명도 균형 맞춰 함께 축소. */}
                    <span className="text-[15px] sm:text-[16px] font-bold text-orange-500 tracking-[0.2em] uppercase">{s.tag}</span>
                    <h3 className="text-[18px] sm:text-[21px] font-bold text-mono-900 leading-snug">{s.title}</h3>
                    <p className="text-[14px] sm:text-[16px] font-normal text-mono-900 leading-[160%] break-keep sm:whitespace-pre-line">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Prev / Next arrows — desktop only */}
            {vLen > 1 && (
              <>
                <button
                  onClick={() => setRawVehicleIdx(i => i - 1)}
                  className="hidden lg:flex absolute left-3 top-[132px] -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full border border-mono-200 items-center justify-center hover:bg-white hover:shadow-card transition-all z-10"
                >
                  <Icon name="chevron-left" size={18} className="text-mono-700" />
                </button>
                <button
                  onClick={() => setRawVehicleIdx(i => i + 1)}
                  className="hidden lg:flex absolute right-3 top-[132px] -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur-sm rounded-full border border-mono-200 items-center justify-center hover:bg-white hover:shadow-card transition-all z-10"
                >
                  <Icon name="chevron-right" size={18} className="text-mono-700" />
                </button>
              </>
            )}

            {/* Dot indicators */}
            {vLen > 1 && (
              <div className="flex items-center justify-center gap-1.5 py-3">
                {vehicleSlides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setRawVehicleIdx(i + 1)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      i === vehicleDisplayIdx ? 'w-5 bg-orange-500' : 'w-1.5 bg-mono-300 hover:bg-mono-400'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* ===== Our Services Card (960px, r:12, bg:#181C26) ===== */}
      <section className="max-w-content mx-auto px-4 sm:px-5 pb-10 sm:pb-16">
        <div className="bg-mono-800 rounded-xl p-4 sm:p-5 lg:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
            {/* Title area spanning 2 columns */}
            <div className="lg:col-span-2 flex flex-col justify-center items-center text-center py-2 sm:py-0">
              <span className="text-sm sm:text-base font-bold text-orange-500 tracking-[0.3em]">{t('transfer_landing.our_services')}</span>
              <h2 className="text-lg sm:text-2xl font-extrabold text-white mt-2 break-keep">{t('transfer_landing.our_services_title')}</h2>
            </div>
            {/* All 4 cards: 283x191, p:20, gap:10, bg:#2E3442, r:12 */}
            {serviceFeatures.map((feat) => (
              <div
                key={feat.title}
                className="group bg-mono-700 rounded-xl p-4 sm:p-5 flex flex-col items-center text-center gap-2.5 transition-all duration-300 hover:bg-mono-600 hover:-translate-y-1 hover:shadow-xl cursor-default"
              >
                {/* Double-circle icon — hover 시 주황색 톤 전환 */}
                <div className="relative w-[52px] h-[52px] sm:w-[68px] sm:h-[68px] shrink-0">
                  <div className="absolute inset-0 rounded-full bg-mono-600 transition-colors duration-300 group-hover:bg-orange-500/20" />
                  <div className="absolute inset-[4px] sm:inset-[5px] rounded-full bg-mono-500 transition-colors duration-300 group-hover:bg-orange-500/40" />
                  <div className="absolute inset-0 flex items-center justify-center transition-colors duration-300 text-white group-hover:text-orange-400">
                    <Icon name={feat.icon} size={30} className="sm:hidden" />
                    <Icon name={feat.icon} size={40} className="hidden sm:block" />
                  </div>
                </div>
                <div className="flex flex-col gap-2 mt-auto items-center text-center">
                  <h3 className="text-[17px] sm:text-lg font-bold text-white leading-snug">{feat.title}</h3>
                  <p className="text-[15px] font-medium text-mono-200 leading-[170%] break-keep whitespace-pre-line">{feat.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Review List Modal */}
      {showReviewModal && <ReviewListModal onClose={() => setShowReviewModal(false)} />}
    </div>
  );
}
